import React, { useState, useRef, useEffect } from "react";
import {
  Col,
  Card,
  CardBody,
  Row,
  Input,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  FormGroup,
  Label,
} from "reactstrap";
import { cof, environment, checkEnvironment } from "../../../api/cof";
import Webcam from "react-webcam";
import Cropper from "react-easy-crop";
import { FaCamera, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./cof.css";

const toggleItems = [
  {
    id: 0,
    label: "Bycatch is minimized (e.g., turtles, seabirds, juvenile fish)",
  },
  {
    id: 1,
    label: "Endangered, Threatened, or Protected (ETP) species are not harmed",
  },
  {
    id: 2,
    label:
      "Fishing gear does not damage habitats (e.g., coral reefs, seagrass beds)",
  },
  { id: 3, label: "Ecosystem structure and function are protected" },
  { id: 4, label: "Efforts are made to reduce ghost gear (lost nets/lines)" },
];

const Environment = ({ sendFunction, requestId, vesselNumber }) => {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [status, setStatus] = useState("Accepted");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [currentToggleId, setCurrentToggleId] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    vesselNumber: vesselNumber,
    requestId: requestId,
    tripId: requestId,
    status: 'Pending',
    toggles: toggleItems.reduce((acc, item) => {
      acc[item.id] = false;
      return acc;
    }, {}),
  });

  const checklist = toggleItems.map((item) => formData.toggles[item.id]);
  const hasChecklistSelection = checklist.some((v) => !!v);
  const hasUploadedImage = Object.values(uploadedImages || {}).some((v) => !!v);
  const isReadyToSubmit = hasChecklistSelection && hasUploadedImage;
  useEffect(() => {
    const fetchEnvironmentData = async () => {
      try {
        const response = await checkEnvironment(requestId);
        console.log('Environment Data:', response);
        setFormData((prev) => ({
          ...prev,
          status: response.data[0].status || prev.status,
          vesselNumber: response.data[0].vesselNumber || prev.vesselNumber,
          toggles: response.data[0].checklist || prev.toggles,
          images: response.data[0].images || prev.images,
        }));
        setUploadedImages(response.data[0].images || {});
      } catch (error) {
        console.log('Failed to fetch Environment data.');
      }
    };
    fetchEnvironmentData();
  }, [requestId]);
  const handleChange = (e) => {
    const { name, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      toggles: {
        ...prevState.toggles,
        [name]: type === 'checkbox' ? checked : prevState.toggles[name],
      },
    }));
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleCameraModal = (toggleId) => {
    setCurrentToggleId(toggleId);
    setIsCameraModalOpen(!isCameraModalOpen);
    setCapturedImage(null);
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    await image.decode();

    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL('image/jpeg');
  };

  const handleImageCapture = async () => {
    if (!capturedImage || !croppedAreaPixels) return;

    const croppedImage = await getCroppedImg(capturedImage, croppedAreaPixels);

    // Save into uploadedImages state
    setUploadedImages((prev) => ({
      ...prev,
      [currentToggleId]: croppedImage,
    }));

    // Reset modal state
    setIsCameraModalOpen(false);
    setCapturedImage(null);
  };

  const handleImageClick = (imageSrc) => {
    setPreviewImage(imageSrc);
  };

  const closePreview = () => {
    setPreviewImage(null);
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    toggleModal();
  };

  const submit = async (newStatus) => {
    setLoading(true);
    const prevStatus = formData.status;
    const finalData = {
      vesselNumber: formData.vesselNumber,
      requestId: formData.requestId,
      tripId: formData.requestId,
      status: newStatus,
      remarks,
      checklist,
      images: uploadedImages,
    };

    try {
      await environment(finalData);

      // optimistic UI update
      setFormData((prev) => ({ ...prev, status: newStatus }));

      if (newStatus === 'Accepted') {
        await Swal.fire({
          title: 'Accepted!',
          text: 'Environment Accepted successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
        });
      } else {
        await Swal.fire({
          title: 'Rejected!',
          text: 'Environment Rejected successfully!',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      }

      toggleModal();
      if (typeof sendFunction === 'function') {
        try {
          await sendFunction(finalData.vesselNumber);
        } catch (err) {
          console.warn('sendFunction threw an error:', err);
        }
      } else {
        console.warn('sendFunction is not provided or not a function');
      }
    } catch (error) {
      console.error('Error submitting form.', error);
      // revert on failure
      setFormData((prev) => ({ ...prev, status: prevStatus }));
      Swal.fire({
        title: 'Error',
        text: 'Failed to submit. Please try again.',
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAllImages = async () => {
    try {
      const formData = new FormData();

      for (const [id, imageUrl] of Object.entries(uploadedImages)) {
        const blob = await (await fetch(imageUrl)).blob();
        formData.append('images[]', blob, `toggle-${id}.jpg`);
      }

      // Debug print
      for (let [key, value] of formData.entries()) {
        console.log('FormData →', key, value.name);
      }

      // Upload (uncomment to enable)
      /*
      const response = await fetch("https://api.starmarketapi.online/upload", {
        method: "POST",
        body: formData,
      });
  
      if (response.ok) {
        console.log("All images uploaded successfully!");
      } else {
        console.error("Image upload failed.");
      }
      */
    } catch (err) {
      console.error('Error uploading images:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const checklistArray = toggleItems.map((item) => formData.toggles[item.id]);
    const payload = {
      vesselNumber: formData.vesselNumber,
      requestId: formData.requestId,
      checklist: checklistArray,
      status: 'Accepted',
      images: uploadedImages,
    };
    try {
      const response = await fetch(
        'https://api.starmarketapi.online/caf/cofDetails',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        console.log('COF submitted successfully!');
      } else {
        console.error('Error submitting COF');
      }
    } catch (error) {
      console.error('Request failed:', error);
    }
  };

  return (
    <React.Fragment>
      <Col lg={8}>
        <Card className="candidate-details mt-lg-0">
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '10px',
              padding: '20px',
              backgroundColor: '#f4f4f4',
            }}
          >
            {formData.status === 'Accepted' ? (
              <h6 className="status-button-success">
                Environment {formData.status}
              </h6>
            ) : formData.status === 'Rejected' ? (
              <h6 className="status-button-danger">
                Environment {formData.status}
              </h6>
            ) : (
              <h6 className="status-button-warning">Environment Pending</h6>
            )}
          </div>
          <CardBody className="candidate-personal-detail mt-3">
            <Row>
              {toggleItems.map((item) => (
                <Row
                  key={item.id}
                  className="align-items-center"
                  style={{
                    background: '#f4f4f4',
                    margin: '6px 2px',
                    borderRadius: '20px',
                  }}
                >
                  <Col
                    lg={8}
                    style={{ padding: '10px' }}
                  >
                    <div className="toggle-card d-flex align-items-center">
                      <div className="number-badge">{item.id + 1}</div>
                      <span className="text flex-grow-1">{item.label}</span>
                    </div>
                  </Col>
                  <Col
                    lg={4}
                    className="d-flex align-items-center justify-content-end"
                    style={{ padding: '10px' }}
                  >
                    <FaCamera
                      style={{ cursor: 'pointer', marginRight: '15px' }}
                      onClick={() => toggleCameraModal(item.id)}
                    />
                    {uploadedImages[item.id] && (
                      <img
                        src={uploadedImages[item.id]}
                        alt={`Uploaded for ${item.label}`}
                        style={{
                          width: '30px',
                          height: '30px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          marginRight: '10px',
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          handleImageClick(uploadedImages[item.id])
                        }
                      />
                    )}
                    <FormGroup
                      switch
                      className="ms-auto custom-switch"
                    >
                      <Input
                        type="switch"
                        id={`switch-${item.id}`}
                        name={item.id}
                        checked={formData.toggles[item.id] || false}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              ))}
            </Row>
            {previewImage && (
              <div
                className="preview-overlay"
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1200,
                }}
                onClick={closePreview}
              >
                <div
                  className="preview-container"
                  style={{
                    color: 'white',
                    padding: '20px',
                    borderRadius: '10px',
                    position: 'relative',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                  onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
                >
                  <FaTimes
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      cursor: 'pointer',
                      fontSize: '20px',
                    }}
                    onClick={closePreview}
                  />
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{
                      width: '700px',
                      height: '700px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              </div>
            )}
            {formData.status !== 'Accepted' &&
              formData.status !== 'Rejected' && (
                <div
                  className="d-flex justify-content-end"
                  style={{ gap: '10px' }}
                >
                  <Button
                    onClick={() => handleStatusChange('Accepted')}
                    color="secondary"
                    className="btn-black"
                    disabled={loading || !isReadyToSubmit}
                    title={
                      !isReadyToSubmit
                        ? 'Select at least one checklist item and upload one image'
                        : ''
                    }
                  >
                    {loading ? 'Submitting...' : 'Accept'}
                  </Button>
                  <Button
                    onClick={() => handleStatusChange('Rejected')}
                    color="danger"
                    className="btn-red"
                    disabled={loading || !isReadyToSubmit}
                    title={
                      !isReadyToSubmit
                        ? 'Select at least one checklist item and upload one image'
                        : ''
                    }
                  >
                    {loading ? 'Submitting...' : 'Reject'}
                  </Button>
                </div>
              )}

            <Modal
              isOpen={isModalOpen}
              toggle={toggleModal}
            >
              <ModalHeader toggle={toggleModal}>
                {status === 'Accepted' ? 'Form Accepted' : 'Form Rejected'}
              </ModalHeader>
              <ModalBody>
                {status === 'Accepted'
                  ? 'You have accepted the COF form. Please confirm your decision.'
                  : 'You have rejected the COF form. Please confirm your decision.'}
                <div className="mt-3">
                  <Label for="remarks">Remarks</Label>
                  <Input
                    type="textarea"
                    id="remarks"
                    name="remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks here"
                    rows="3"
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onClick={() => submit(status)}
                >
                  Confirm
                </Button>
                <Button
                  color="secondary"
                  onClick={toggleModal}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              isOpen={isCameraModalOpen}
              toggle={() => toggleCameraModal(null)}
              size="lg"
            >
              <ModalHeader toggle={() => toggleCameraModal(null)}>
                Capture and Crop Photo
              </ModalHeader>
              <ModalBody>
                {!capturedImage ? (
                  <div>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      width="100%"
                      videoConstraints={{ facingMode: 'environment' }}
                    />
                    <Button
                      color="primary"
                      onClick={capture}
                      style={{ marginTop: '10px' }}
                    >
                      Capture Photo
                    </Button>
                  </div>
                ) : (
                  <div style={{ position: 'relative', height: '400px' }}>
                    <Cropper
                      image={capturedImage}
                      crop={crop}
                      zoom={zoom}
                      aspect={4 / 3}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                {capturedImage && (
                  <>
                    <Button
                      color="primary"
                      onClick={handleImageCapture}
                    >
                      Upload Cropped Image
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() => setCapturedImage(null)}
                    >
                      Retake
                    </Button>
                  </>
                )}
                <Button
                  color="secondary"
                  onClick={() => toggleCameraModal(null)}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default Environment;
